import classNames from "classnames/dedupe";
import React, {
  CSSProperties,
  FC,
  useCallback,
  useEffect,
  ReactNode,
} from "react";
import ReactDOM from "react-dom";
import useEventListener from "../../../hooks/useEventListener";
import useIsMounted from "../../../hooks/useIsMounted";
import { ExitIcon } from "../../../icons";
import { Button } from "../../ActionsSelectors/Button";

export interface IModalProps {
  /**
   * Indicates if element is present in DOM
   */
  isOpen?: boolean;

  /**
   * Styles for close button. Used for animation behavior.
   */
  exitBtnStyle?: CSSProperties;

  /**
   * Set parent container element, default is document.body
   */
  container?: Element;

  classes?: {
    root?: string;
    exitButton?: string;
  };
  children?: ReactNode;

  /**
   * A callback function for when modal window is closed.
   *
   */
  onClose?(): void;
}

const Modal: FC<IModalProps> = ({
  exitBtnStyle,
  isOpen,
  children,
  container = typeof window === "undefined" ? null : document.body,
  classes,
  onClose,
}) => {
  const isMounted = useIsMounted();

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.code === "Escape") {
        onClose && onClose();
      }
    },
    [onClose],
  );

  useEventListener("keydown", handleKeyDown);

  useEffect(() => {
    isOpen && isMounted && (document.body.style.overflow = "hidden");
    return () => {
      document.body.style.overflow = "initial";
    };
  }, [isOpen, isMounted]);

  const handleClick = useCallback(() => onClose && onClose(), [onClose]);

  const rootClasses = classNames("modal", classes?.root);
  const exitButtonClasses = classNames("modal__exit", classes?.exitButton);
  const modal = (
    <div className={rootClasses} data-testid="modal-window">
      <Button
        variant="icon"
        classes={{ root: exitButtonClasses }}
        style={exitBtnStyle}
        onClick={handleClick}
      >
        <ExitIcon />
      </Button>
      {children}
    </div>
  );

  return isOpen && isMounted() && container
    ? ReactDOM.createPortal(modal, container)
    : null;
};

export default Modal;
